import React from "react"
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api"
import { GoogleMapKey } from "../../../app/helpers/MapHelper.js"
import { Skeleton } from "@material-ui/lab"
import { toAbsoluteUrl } from "../../../_metronic/_helpers/index.js"
import { ICON_CONSTANTS } from "../../../constants/ImageConstants.js"
import { useEffect, useRef, useState } from "react"

export const BaseMapSelector = ({
  latitude,
  longitude,
  className = "",
  zoom = 15,
  options,
  isSelector = false,
  onLocationSelect,
  editable = true,
  id = "base-map-selector",
  customMarkers,
  customContainerStyle,
  showCurrentLocationButton = false,
  onCurrentLocationClick,
  onMapLoad,
  recenterPosition,
  icon
}) => {
  const defaultOptions = {
    zoomControl: true,
    streetViewControl: false,
    cameraControl: false,
    clickableIcons: false,
    disableDefaultUI: true
  }
  const containerStyle = {
    width: "100%",
    height: "400px"
  }

  const mapRef = useRef(null)
  const onCurrentLocationClickRef = useRef(onCurrentLocationClick)
  useEffect(() => {
    onCurrentLocationClickRef.current = onCurrentLocationClick
  }, [onCurrentLocationClick])

  const [center, setCenter] = useState({
    lat: latitude ? parseFloat(latitude) : -6.2088,
    lng: longitude ? parseFloat(longitude) : 106.8456
  })

  const [markerPosition, setMarkerPosition] = useState({
    lat: latitude ? parseFloat(latitude) : -6.2088,
    lng: longitude ? parseFloat(longitude) : 106.8456
  })

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: GoogleMapKey()
  })

  useEffect(() => {
    // If latitude and longitude are provided, use them
    if (latitude && longitude) {
      const position = {
        lat: parseFloat(latitude),
        lng: parseFloat(longitude)
      }
      setCenter(position)
      setMarkerPosition(position)
    } else if (!isSelector) {
      // For non-selector (preview) mode, try to get device location for display only
      navigator?.geolocation.getCurrentPosition(
        ({ coords: { latitude: lat, longitude: lng } }) => {
          const position = { lat, lng }
          setCenter(position)
          setMarkerPosition(position)
        },
        () => {
          // Fallback to default location if geolocation fails
          const fallbackPosition = {
            lat: -6.2088,
            lng: 106.8456
          }
          setCenter(fallbackPosition)
          setMarkerPosition(fallbackPosition)
        }
      )
    } else {
      const fallbackPosition = {
        lat: -6.2088,
        lng: 106.8456
      }
      setCenter(fallbackPosition)
      setMarkerPosition(fallbackPosition)
    }
  }, [latitude, longitude, isSelector])

  // Recenter map view only – does NOT move the marker
  useEffect(() => {
    if (recenterPosition?.lat && recenterPosition?.lng) {
      if (mapRef.current) {
        mapRef.current.panTo({ lat: recenterPosition.lat, lng: recenterPosition.lng })
      }
    }
  }, [recenterPosition])

  const handleMapClick = (event) => {
    if (isSelector && editable) {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
      setMarkerPosition(newPosition)

      if (onLocationSelect) {
        onLocationSelect(newPosition)
      }
    }
  }

  const handleMarkerDragEnd = (event) => {
    if (isSelector && editable) {
      const newPosition = {
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      }
      setMarkerPosition(newPosition)

      if (onLocationSelect) {
        onLocationSelect(newPosition)
      }
    }
  }

  if (!isLoaded) {
    return <Skeleton width={containerStyle.width} height={containerStyle.height} />
  }

  const defaultMarkers = () => {
    if (customMarkers) {
      return customMarkers()
    }
    return (
      <Marker
        position={markerPosition}
        icon={toAbsoluteUrl(icon ?? ICON_CONSTANTS.MARKER_BASECAMP)}
        draggable={isSelector && editable}
        onDragEnd={handleMarkerDragEnd}
      />
    )
  }

  return (
    <div style={{ position: "relative" }}>
      <GoogleMap
        id={id}
        mapContainerStyle={{ ...containerStyle, ...customContainerStyle }}
        center={center}
        zoom={zoom}
        mapContainerClassName={className}
        options={
          options
            ? {
                ...defaultOptions,
                ...options,
                gestureHandling: isSelector ? "auto" : "none",
                disableDefaultUI: !isSelector
              }
            : {
                ...defaultOptions,
                gestureHandling: isSelector ? "auto" : "none",
                disableDefaultUI: !isSelector
              }
        }
        mapTypeId={"satellite"}
        onLoad={(map) => {
          mapRef.current = map
          if (onMapLoad) onMapLoad(map)
          if (showCurrentLocationButton) {
            const btn = document.createElement("button")
            btn.type = "button"
            btn.title = "Use current location"
            btn.style.cssText =
              "width:40px;height:40px;background:#fff;border:none;border-radius:8px;" +
              "box-shadow:0 2px 8px rgba(0,0,0,0.14);display:flex;align-items:center;" +
              "justify-content:center;cursor:pointer;padding:0;margin:0 10px 0 0;"
            const img = document.createElement("img")
            img.src = toAbsoluteUrl(ICON_CONSTANTS.FOCUS)
            img.alt = "current location"
            img.style.cssText = "width:20px;height:20px;"
            btn.appendChild(img)
            btn.addEventListener("click", () => {
              if (onCurrentLocationClickRef.current) onCurrentLocationClickRef.current()
            })
            map.controls[window.google.maps.ControlPosition.RIGHT_BOTTOM].push(btn)
          }
        }}
        onClick={handleMapClick}>
        {defaultMarkers()}
      </GoogleMap>

    </div>
  )
}
